import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { Card, Row, Col, Button, Typography, Space, Alert, Tooltip } from 'antd';
import { SettingOutlined, WarningOutlined, EnvironmentOutlined, ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../components/PageHeader';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
// Map Components
import MapCore from '../components/mapping/MapCore';
import SiteSelectionPanel from '../components/mapping/SiteSelectionPanel';
import AdvancedDrawingTools from '../components/mapping/AdvancedDrawingTools';
import HazardOverlayLayer from '../components/mapping/HazardOverlayLayer';
import MapLoadingOverlay from '../components/mapping/MapLoadingOverlay';
import MapLegend from '../components/mapping/MapLegend';
import SiteListPopup from '../components/mapping/SiteListPopup';
import EnterpriseMetricsBar from '../components/EnterpriseMetricsBar';
import { useSiteMetrics } from '../hooks/useDashboardMetrics';
// Dashboard styles
import '../styles/Dashboard.css';

const { Title, Text } = Typography;

const Dashboard = () => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const navigate = useNavigate();
  
  // Get working site metrics (this is the data that already works!)
  const siteMetrics = useSiteMetrics(currentUser?.orgId);
  
  const [allSites, setAllSites] = useState([]);
  const [siteCounts, setSiteCounts] = useState(null);
  
  // 🎯 DASHBOARD FIX: Popup state for proper positioning
  const [showSitePopup, setShowSitePopup] = useState(false);
  const [sitePopupData, setSitePopupData] = useState(null);

  // DEBUG: Log both data sources
  useEffect(() => {
    console.log('🔍 Dashboard DEBUG - siteMetrics:', siteMetrics);
    console.log('🔍 Dashboard DEBUG - allSites length:', allSites.length);
    if (allSites.length > 0) {
      console.log('🔍 Dashboard DEBUG - first allSite:', allSites[0]);
    }
  }, [siteMetrics, allSites]);
  const [organisation, setOrganisation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState([]);

  // Map state management (copied from Map.jsx)
  const [selectedSites, setSelectedSites] = useState([]);
  const [mapKey, setMapKey] = useState(0);
  const [mapInstance, setMapInstance] = useState(null);

  
  // Debug allSites changes
  useEffect(() => {
    console.log('🔍 Dashboard: allSites state changed to:', allSites.length, 'sites');
    console.log('🔍 Dashboard: siteCounts changed to:', siteCounts);
  }, [allSites, siteCounts]);
  const [measurementMode, setMeasurementMode] = useState('idle');
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [hazardCount, setHazardCount] = useState(0);
  const [showSiteIcons, setShowSiteIcons] = useState(true);
  const [siteColorMode, setSiteColorMode] = useState('status');
  
  // 🌂 UMBRELLA DEBUG: Track state changes
  useEffect(() => {
    console.log('🌂 UMBRELLA DASHBOARD: siteColorMode state changed to:', siteColorMode);
  }, [siteColorMode]);
  
  // 🌂 UMBRELLA DEBUG: Wrap setSiteColorMode to track calls
  const handleSiteColorModeChange = useCallback((newMode) => {
    console.log('🌂 UMBRELLA DASHBOARD: setSiteColorMode called with:', newMode);
    setSiteColorMode(newMode);
  }, []);
  
  // Unit preference state with localStorage persistence
  const [unitPreference, setUnitPreference] = useState(() => {
    try {
      const saved = localStorage.getItem('crisisCompass_unitPreference');
      return saved ? JSON.parse(saved) : 'km';
    } catch (error) {
      console.warn('📏 Failed to load unit preference from localStorage:', error);
      return 'km';
    }
  });

  // Ring styling configuration with localStorage persistence
  const [ringStyleConfig, setRingStyleConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('crisisCompass_ringStyleConfig');
      return saved ? JSON.parse(saved) : {
        colorScheme: 'red',
        customColors: {
          primary: '#ff0000',
          rings: '#ff0000'
        },
        opacity: {
          primary: 0.8,
          rings: 0.4,
          fill: 0.1
        },
        strokeWeight: {
          primary: 2,
          rings: 1
        }
      };
    } catch (error) {
      console.warn('🎨 Failed to load ring style config from localStorage:', error);
      return {
        colorScheme: 'red',
        customColors: {
          primary: '#ff0000',
          rings: '#ff0000'
        },
        opacity: {
          primary: 0.8,
          rings: 0.4,
          fill: 0.1
        },
        strokeWeight: {
          primary: 2,
          rings: 1
        }
      };
    }
  });

  // Reference to AdvancedDrawingTools for shape visibility synchronization
  const advancedDrawingToolsRef = useRef(null);

  // Original Dashboard data fetching
  useEffect(() => {
    const fetchOrganisation = async () => {
      if (currentUser?.orgId) {
        try {
          console.log('DEBUG - Current user data:', {
            firstName: currentUser.firstName,
            displayName: currentUser.displayName,
            email: currentUser.email,
            raw: currentUser
          });
          const orgDoc = await getDoc(doc(db, 'c3_organisations', currentUser.orgId));
          if (orgDoc.exists()) {
            setOrganisation(orgDoc.data());
          }
        } catch (error) {
          console.error('Error fetching organisation:', error);
        }
      }
      setLoading(false);
    };

    const fetchIncidents = async () => {
      if (currentUser?.orgId) {
        try {
          const incidentsRef = collection(db, 'c3_incidents');
          const q = query(
            incidentsRef,
            where('orgId', '==', currentUser.orgId)
          );
          const querySnapshot = await getDocs(q);
          const incidentsList = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setIncidents(incidentsList);
        } catch (error) {
          console.error('Error fetching incidents:', error);
        }
      }
    };

    fetchOrganisation();
    fetchIncidents();
  }, [currentUser, db]);

  // Map-specific handlers
  const handleUnitPreferenceChange = (newUnit) => {
    setUnitPreference(newUnit);
    try {
      localStorage.setItem('crisisCompass_unitPreference', JSON.stringify(newUnit));
      console.log('📏 Unit preference saved to localStorage:', newUnit);
    } catch (error) {
      console.warn('📏 Failed to save unit preference to localStorage:', error);
    }
  };

  const handleRingStyleChange = (newConfig) => {
    setRingStyleConfig(newConfig);
    try {
      localStorage.setItem('crisisCompass_ringStyleConfig', JSON.stringify(newConfig));
      console.log('🎨 Ring style config saved to localStorage:', newConfig);
    } catch (error) {
      console.warn('🎨 Failed to save ring style config to localStorage:', error);
    }
  };

  const handleSitesSelected = (sites) => {
    setSelectedSites(sites);
  };

  const handleRefresh = () => {
    setMapKey(prev => prev + 1);
    setSelectedSites([]);
  };

  // Stable callback to prevent useEffect dependency issues in MapCore
  const handleSitesLoaded = useCallback((sites, counts) => {
    console.log('🔍 Dashboard: *** CALLBACK FIRED *** Sites loaded from MapCore:', sites.length, 'sites with counts:', counts);
    setAllSites(sites);
    setSiteCounts(counts);
  }, []);

  // 🎯 FIXED: Circle detection callback - NO DEPS for latest values
  const handleCircleComplete = (circle) => {
    console.log('🎯 Dashboard: handleCircleComplete called with circle:', circle);
    console.log('🎯 Dashboard: Current mapInstance:', !!mapInstance);
    console.log('🎯 Dashboard: measurementFunctionsRef available:', !!mapInstance?.measurementFunctionsRef);
    console.log('🎯 Dashboard: measurementFunctionsRef.current:', !!mapInstance?.measurementFunctionsRef?.current);
    
    const handler = mapInstance?.measurementFunctionsRef?.current?.handleCircleComplete;
    console.log('🎯 Dashboard: handleCircleComplete handler found:', !!handler, typeof handler);
    
    if (!handler) {
      console.log('🚨 Dashboard: handleCircleComplete not ready, mapInstance:', !!mapInstance, 'measurementFunctionsRef:', !!mapInstance?.measurementFunctionsRef?.current);
      return;
    }
    
    console.log('🎯 Dashboard: Calling handleCircleComplete with circle:', circle);
    return handler(circle);
  };

  // 🎯 DASHBOARD FIX: Popup state callbacks for proper positioning  
  const handleSitePopupStateChange = (isVisible) => {
    console.log('🎯 Dashboard: Popup state changed to:', isVisible);
    setShowSitePopup(isVisible);
  };

  const handleSitePopupDataChange = (popupData) => {
    console.log('🎯 Dashboard: Popup data changed:', popupData);
    setSitePopupData(popupData);
  };
  
  // DEBUG: Check if callback is valid
  console.log('🔍 Dashboard: handleSitesLoaded callback defined?', !!handleSitesLoaded, typeof handleSitesLoaded);

  // Calculated metrics
  const showOnboardingButton = (currentUser?.role === 'org_admin' && !organisation?.onboardingComplete) || currentUser?.isAdmin;


  // Check if user has access to map
  if (!currentUser?.orgId) {
    return (
      <Alert
        message="Access Denied"
        description="You need to be part of an organisation to access the dashboard."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed Top Section - Header + Metrics */}
      <div style={{ flexShrink: 0 }} className="p-4">
        {/* Page Header with org badge, role badge, user email, and logout */}
        <PageHeader 
          title={`Dashboard - ${currentUser?.firstName?.split(' ')[0] || 'User'}`}
        />

        {/* Enterprise-Level Metrics Bar */}
        <div style={{ paddingTop: '16px' }}>
          <EnterpriseMetricsBar orgId={currentUser?.orgId} />
        </div>
      </div>

      {/* Map Section - Fills remaining viewport to bottom */}
      <div className="dashboard-map-section" style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
        gap: window.innerWidth <= 768 ? '16px' : '24px', 
        padding: window.innerWidth <= 768 ? '0 16px 16px 16px' : '0 24px 24px 24px' 
      }}>
        {/* Left Sidebar - Drawing Tools - Mobile: Full width, Desktop: Fixed width */}
        <div className="dashboard-left-sidebar" style={{ 
          width: window.innerWidth <= 768 ? '100%' : '300px', 
          flexShrink: 0, 
          // 📱 MOBILE FIX: Remove overflow constraint so hazard container can expand naturally
          ...(window.innerWidth <= 768 ? {
            minHeight: '400px', // Ensure minimum height on mobile
            height: 'auto', // Allow natural expansion
            overflow: 'visible' // Let content expand naturally without scrolling
          } : {
            overflowY: 'auto' // Keep desktop scrolling behavior
          })
        }}>
          {/* 🛠️ CRITICAL FIX: Restore Drawing Tools on mobile - user NEEDS these for mission critical operations */}
            <AdvancedDrawingTools
              ref={advancedDrawingToolsRef}
            map={window.innerWidth <= 768 ? (window.simpleMobileMapInstance || mapInstance) : mapInstance}
              onShapeComplete={(bounds, mode) => {
                console.log('Shape completed:', bounds, mode);
              }}
              onSelectionChange={(bounds, mode) => {
                console.log('Selection changed:', bounds, mode);
              }}
              measurementMode={measurementMode}
              onStartMeasurement={mapInstance?.measurementFunctionsRef?.current?.startMeasurement}
              onClearMeasurement={mapInstance?.measurementFunctionsRef?.current?.clearMeasurement}
              measurementFunctionsRef={mapInstance?.measurementFunctionsRef}
              unitPreference={unitPreference}
              onUnitPreferenceChange={handleUnitPreferenceChange}
              ringStyleConfig={ringStyleConfig}
              onRingStyleChange={handleRingStyleChange}
            onCircleComplete={handleCircleComplete}
            />
          
          <div style={{ marginTop: window.innerWidth <= 768 ? '0' : '16px' }}>
            <HazardOverlayLayer
              map={mapInstance}
              showControls={true}
              onHazardClick={(hazard) => {
                  console.log('Dashboard hazard clicked:', hazard);
              }}
              onHazardCountChange={(count) => {
                console.log('🚨 Dashboard hazard count changed:', count);
                setHazardCount(count);
              }}
                // Site icons toggle control
              showSiteIcons={showSiteIcons}
              onShowSiteIconsChange={setShowSiteIcons}
                // Site color mode toggle control
              siteColorMode={siteColorMode}
              onSiteColorModeChange={handleSiteColorModeChange}
            />
          </div>
        </div>

        {/* Map Section - Fills remaining space */}
        <div className="dashboard-map-container" style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          position: 'relative', // 🎯 DASHBOARD FIX: Enable absolute positioning for popup
          // 📱 MOBILE: Move map down to leave proper space for hazard container
          ...(window.innerWidth <= 768 && {
            marginTop: '80px'
          })
        }}>
          <div style={{ flex: 1 }}>
            <MapCore
              key={mapKey}
              mapId="main-map"
              height={window.innerWidth <= 768 ? "400px" : "100%"}
              showDrawingTools={window.innerWidth > 768}
              showSiteSelection={true}
              onSitesSelected={handleSitesSelected}
              onMapLoad={(mapInstance) => {
                console.log('🗺️ Dashboard.jsx: Map loaded successfully');
                setMapInstance(mapInstance);
                setShowLoadingOverlay(true);
                
                // Auto-hide loading overlay after 5 seconds if not hidden by hazard count
                setTimeout(() => {
                  setShowLoadingOverlay(false);
                }, 5000);
              }}
              onSitesLoaded={handleSitesLoaded}
              onMeasurementModeChange={(mode) => {
                console.log('🗺️ Dashboard.jsx: Measurement mode changed to:', mode);
                setMeasurementMode(mode);
              }}
              unitPreference={unitPreference}
              ringStyleConfig={ringStyleConfig}
              useAdvancedDrawingTools={window.innerWidth > 768}
              showSiteIcons={showSiteIcons}
              siteColorMode={siteColorMode}
              onCircleComplete={handleCircleComplete}
              onSitePopupStateChange={handleSitePopupStateChange}
              onSitePopupDataChange={handleSitePopupDataChange}
              className="main-map"
            />
            
            <MapLoadingOverlay 
              visible={showLoadingOverlay} 
              hazardCount={hazardCount}
            />
          </div>
          
          {/* 🎯 CRITICAL FIX: DISABLED Dashboard popup to prevent dual popups - MapCore handles popup now */}
          {false && showSitePopup && sitePopupData && (
            <div style={{ 
              position: 'absolute',
              top: '50px', // 50px from top of dashboard-map-container
              right: '20px', 
              zIndex: 1000,
              maxWidth: '400px',
              maxHeight: 'calc(100% - 90px)', // Relative to dashboard-map-container height
              overflow: 'auto'
            }}>
              <SiteListPopup
                sites={sitePopupData.sites}
                circle={sitePopupData.circle}
                onClose={sitePopupData.onClose}
              />
            </div>
          )}
          
          {/* Site Status Legend - Below Map */}
          {showSiteIcons && (
            <div style={{ flexShrink: 0, paddingTop: '8px' }}>
              <MapLegend 
                siteColorMode={siteColorMode} 
                sites={allSites}
                siteCounts={siteCounts} // Pass working site counts directly from MapCore
                context="dashboard"
                key={`legend-${allSites.length}`} // Force re-render when sites change
              />
            </div>
          )}
        </div>

        {/* Right Sidebar - Site Selection Panel - Mobile: Full width below map, Desktop: Fixed width */}
        {selectedSites.length > 0 && (
          <div className="dashboard-right-sidebar" style={{ 
            width: window.innerWidth <= 768 ? '100%' : '300px', 
            flexShrink: 0, 
            overflowY: 'auto',
            maxHeight: window.innerWidth <= 768 ? '300px' : 'none'
          }}>
            <SiteSelectionPanel
              selectedSites={selectedSites}
              showMassContact={true}
              title="Selected Sites"
              emptyMessage="Draw a shape on the map to select sites for mass communication."
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
